import { A11YFormatService, ConfigSettingsService, ResourceProvider } from '../providers';
import { AnhangDtoModel, AntragDtoModel, AntragsInfoExtended, TokenState } from '../models';
import { AntragStatus } from '../enums';
import { AntragSupportingDataService } from './antrag-supporting-data.service';
import { Injectable, inject } from '@angular/core';
import { Observable, firstValueFrom, forkJoin } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { formatDate } from '@angular/common';
import { map } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class AntragHelperService {
  private readonly a11yFormatService = inject(A11YFormatService);
  private readonly configService = inject(ConfigSettingsService);
  private readonly resourceProvider = inject(ResourceProvider);
  private readonly antragSupportingDataService = inject(AntragSupportingDataService);
  private readonly translateService = inject(TranslateService);

  private incompleteStates = toSignal(this.antragSupportingDataService.incompleteStates, { initialValue: [] });

  public getStateI18nKey(state: number): Observable<string> {
    return this.antragSupportingDataService
      .getStateTextRepresentation(state)
      .pipe(map((t) => `antrags_uebersicht.server.antrags_status.${t}`));
  }

  public canDeleteAntrag(status: number): Observable<boolean> {
    return this.antragSupportingDataService.canAntragBeDeleted(status);
  }

  public hasDifferentToken(status: TokenState): Observable<boolean> {
    return this.antragSupportingDataService.hasEncryptionIssue(status);
  }

  public sortAntragSeiten(antragsseiteInfo: AnhangDtoModel[]): AnhangDtoModel[][] {
    const belegeWithSeiten: AnhangDtoModel[][] = [];

    for (let i = 0, len = antragsseiteInfo.length; i < len; i++) {
      const seite = antragsseiteInfo[i];
      if (!belegeWithSeiten[seite.belegNr - 1]) {
        belegeWithSeiten[seite.belegNr - 1] = [];
      }
      belegeWithSeiten[seite.belegNr - 1].push(seite);
    }
    belegeWithSeiten.forEach((value) => value.sort((a, b1) => a.seitenNr - b1.seitenNr));

    return belegeWithSeiten;
  }

  public filterForDifferentEinreichungsVersions(antrag: AntragDtoModel): boolean {
    const einreichungV3Activated = this.configService.appSettings.einreichungV3Activated;
    const allowPdfUpload = this.configService.appSettings.customizing.allowPdfUpload;

    if (!einreichungV3Activated && !allowPdfUpload) {
      return this.hasPdfPage(antrag);
    }

    return false;
  }

  public getDraftCount(): Promise<number> {
    return firstValueFrom(this.filterAndCountAntrage(this.antragSupportingDataService.incompleteStates));
  }

  public getFinishedAntragCount(): Promise<number> {
    return firstValueFrom(this.filterAndCountAntrage(this.antragSupportingDataService.completeStates));
  }

  public async turnIntoAntragsInfoExtended(antrag: AntragDtoModel): Promise<AntragsInfoExtended> {
    const isAntragDeletable = await firstValueFrom(this.canDeleteAntrag(antrag.status));
    const cannotDecrypt = await firstValueFrom(this.hasDifferentToken(antrag.tokenState));
    const antragsSize = (antrag.seitenBytesTotal / 1024 / 1024).toFixed(2);
    const hasDedicatedFirst = antrag.flags?.includes('HasDedicatedFirst') ?? false;
    const { cssClass, icon } = this.antragSupportingDataService.getCssAndIconForState(antrag.status);

    return {
      ...antrag,
      hasDedicatedFirst,
      belege: this.sortAntragSeiten(antrag.antragsseiteInfo),
      canDeleteAntrag: isAntragDeletable,
      cannotDecrypt,
      antragsSize,
      statusTypeCss: cssClass,
      statusTypeIcon: icon,
      title: this.getTitle(antrag),
      titleDate: this.getTitleDate(antrag),
      titleBelege: this.getTitleBelege(antrag, cannotDecrypt, hasDedicatedFirst, antragsSize),
      titleTotalA11y: `${this.getTitle(antrag, true)} ${this.getTitleBelege(antrag, cannotDecrypt, hasDedicatedFirst, antragsSize, true)}`,
      isLangantrag: antrag.isLangantrag,
      isFeatureDedicatedBelegEnabledOnAntrag: antrag.isFeatureDedicatedBelegEnabledOnAntrag,
    };
  }

  public isDraftAntrag(status: AntragStatus): boolean {
    return this.incompleteStates().includes(status);
  }

  private hasPdfPage(antrag: AntragDtoModel): boolean {
    return antrag.antragsseiteInfo.some((seite) => {
      return seite.dataType === 'pdf';
    });
  }

  private filterAndCountAntrage(statesToFilerBy: Observable<number[]>): Observable<number> {
    return forkJoin([statesToFilerBy, this.resourceProvider.getAntragsHistory(true)]).pipe(
      map(([states, antraege]) =>
        antraege.filter(
          (antrag) => states.includes(antrag.status) && !this.filterForDifferentEinreichungsVersions(antrag),
        ),
      ),
      map((a) => a.length),
    );
  }

  private getTitle(antrag: AntragDtoModel, a11yMode = false): string {
    const einreichungV3Activated = this.configService.appSettings.einreichungV3Activated;

    if (einreichungV3Activated && antrag.containerName) {
      return `${antrag.containerName} ${a11yMode ? this.a11yFormatService.dateToA11yString(antrag.hochgeladen, 'd m Y') : ''}`;
    }

    if (this.isDraftAntrag(antrag.status)) {
      return this.translateService.instant(_('antrags_uebersicht.label.draft'), {
        date: a11yMode
          ? this.a11yFormatService.dateToA11yString(antrag.concurrency, 'd m Y')
          : formatDate(antrag.concurrency, 'shortDate', 'de'),
      });
    }

    return this.translateService.instant(a11yMode ? 'a11y.antrag.title' : 'antrags_uebersicht.label.title', {
      date: a11yMode
        ? this.a11yFormatService.dateToA11yString(antrag.hochgeladen, 'd m Y')
        : formatDate(antrag.hochgeladen, 'shortDate', 'de'),
    });
  }

  private getTitleDate(antrag: AntragDtoModel): string {
    const label = this.isDraftAntrag(antrag.status) ? 'title_created' : 'title_submitted';
    const date = antrag.hochgeladen || antrag.angelegt;

    return this.translateService.instant(_(`antrags_uebersicht.label.${label}`), {
      date: formatDate(date, 'shortDate', 'de'),
    });
  }

  private getTitleBelege(
    antrag: AntragDtoModel,
    cannotDecrypt: boolean,
    hasDedicatedFirst: boolean,
    antragsSize: string,
    a11yMode = false,
  ): string {
    if (cannotDecrypt) {
      return this.translateService.instant(_('antrags_uebersicht.label.auto_decrypting'));
    }

    if (hasDedicatedFirst) {
      const a11yTitle = _('a11y.antrag.has_dedicated_first');
      const title = _('antrags_uebersicht.label.has_dedicated_first');

      const translated = this.translateService.instant(`${a11yMode ? a11yTitle : title}`, {
        numberOfBelege: antrag.anzBelege - 1,
        numberOfSeiten: antrag.anzSeiten,
        sizeInMB: a11yMode ? this.a11yFormatService.numberToA11yString(antragsSize) : antragsSize,
      });
      return translated;
    }

    const a11yTitle = _('a11y.antrag.title_count');
    const title = _('antrags_uebersicht.label.title_count');

    const translated = this.translateService.instant(`${a11yMode ? a11yTitle : title}`, {
      numberOfBelege: antrag.anzBelege,
      numberOfSeiten: antrag.anzSeiten,
      sizeInMB: a11yMode ? this.a11yFormatService.numberToA11yString(antragsSize) : antragsSize,
    });
    return translated;
  }
}
